<template>
  <a
      class="consentLink"
      href="javascript:void(0)"
      v-bind="$attrs"
      @click="open"
  >
    <slot/>
  </a>

  <teleport to="body">
    <div v-if="visible" class="consentModal">
      <div class="consentModal__box">
        <div class="consentModal__head">
          <div class="consentModal__title">{{ modalTitle }}</div>
          <button class="consentModal__close" title="Закрыть" @click="close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="consentModal__body">
          <MainPreloader
              v-if="loading"
              position="fixed"
              :size="80"
              :stroke="3"
          />
          <div
              v-else
              class="consentModal__content"
              v-html="content"
          ></div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import MainPreloader from "@/components/shared/MainPreloader.vue";
import {useCustomPagesStore} from "@/store/custom-pages";

const DEFAULT_TITLES = {
  'consent-personal': 'Согласие на обработку персональных данных',
  'consent-info': 'Согласие на получение рекламно-информационных сообщений',
  'consent-club': 'Устав и условия Клуба КиберДеда',
  'consent-confidential': 'Политика конфиденциальности',
};

export default {
  name: "ConsentLink",
  inheritAttrs: false,
  components: {MainPreloader},
  props: {
    slug: {type: String, required: true},
    title: {type: String, default: ''},
  },
  data() {
    return {
      visible: false,
    };
  },
  computed: {
    modalTitle() {
      return this.title || DEFAULT_TITLES[this.slug] || 'Согласие';
    },
    customPagesStore() {
      return useCustomPagesStore();
    },
    loading() {
      return this.customPagesStore.loading;
    },
    content() {
      return this.customPagesStore.record?.content || '';
    },
  },
  methods: {
    open() {
      this.visible = true;
      this.lockBodyScroll(true);
      this.customPagesStore.loadCustomPage({page_slug: this.slug});
    },
    close() {
      this.visible = false;
      this.lockBodyScroll(false);
    },
    lockBodyScroll(lock) {
      document.body.style.overflow = lock ? 'hidden' : '';
      if (lock) {
        document.body.classList.add('consentModal--lock');
      } else {
        document.body.classList.remove('consentModal--lock');
      }
    },
    // Перехватываем Escape на этапе capture (раньше, чем до него дойдёт
    // событие до popper'а панели/меню) — чтобы Esc закрывал только модалку.
    onKeydown(e) {
      if (e.key === 'Escape' && this.visible) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    onKeyup(e) {
      if (e.key === 'Escape' && this.visible) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown, true);
    document.addEventListener('keyup', this.onKeyup, true);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown, true);
    document.removeEventListener('keyup', this.onKeyup, true);
  },
};
</script>

<style lang="scss">
.consentModal--lock {
  pointer-events: none;

  .consentModal {
    pointer-events: auto;
  }
}
</style>

<style scoped lang="scss">
.consentLink {
  cursor: pointer;
}

.consentModal {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 60px 16px 16px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.55);

  &__box {
    background: var(--cd-card-bg);
    border-radius: 20px;
    padding: 32px;
    width: 100%;
    max-width: 960px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: left;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  &__title {
    color: var(--cd-base-green);
    font-size: 20px;
    font-family: var(--cd-default-font);
  }

  &__close {
    background: transparent;
    border: none;
    color: var(--cd-text-muted);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border-radius: 6px;
    transition: color 0.3s;

    &:hover {
      color: var(--cd-danger);
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-height: 75vh;
    overflow-y: auto;
  }

  &__content {
    font-family: var(--cd-default-font);
    font-size: 15px;
    line-height: 1.6;
    color: var(--cd-text-muted);

    :deep(p),
    :deep(ul),
    :deep(ol),
    :deep(blockquote) {
      margin: 0 0 1em;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
      margin: 1.25em 0 0.5em;
    }

    :deep(li + li) {
      margin-top: 0.25em;
    }

    :deep(a) {
      color: var(--cd-highlight-text);
    }
  }
}
</style>
